import core, { View } from '@gin-betting/core';
import { createSelector } from 'reselect';

import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import { useMemoSelector } from 'hooks';
import { selectRouteBySportEvent } from 'selectors';
import { ParentPath } from 'types';
import {
    marketPropsToPick,
    matchPropsToPick,
    oddsPropsToPick,
    tournamentPropsToPick,
} from '../../constants';

interface Input {
    matchId: string | null;
}

interface Output {
    match: Pick<View.Match, typeof matchPropsToPick[number]> | null;
    market: Pick<View.Market, typeof marketPropsToPick[number]> | null;
    odds: Array<Pick<View.Odd, typeof oddsPropsToPick[number]>>;
    tournament: Pick<
        View.Tournament,
        typeof tournamentPropsToPick[number]
    > | null;
    competitors: View.CompetitorsValid;
    route: ParentPath;
    isLive: boolean;
}

const {
    entities: {
        selectors: {
            selectMatchCompetitorsValid,
            makeMatchSelector,
            makeMarketSelector,
            makeOddsByMarketSelector,
            makeTournamentSelector,
        },
    },
} = core;

const selectMatch = makeMatchSelector({
    propsToPick: matchPropsToPick,
});

const selectMarket = makeMarketSelector({
    propsToPick: marketPropsToPick,
});

const selectOdds = makeOddsByMarketSelector({
    marketSelector: selectMarket,
    propsToPick: oddsPropsToPick,
});

const selectTournament = makeTournamentSelector({
    propsToPick: tournamentPropsToPick,
    selectTournamentId: createSelector(
        selectMatch,
        match => match?.tournament ?? null
    ),
});

const selectProps = createSelector(
    selectMatch,
    selectMarket,
    selectOdds,
    selectTournament,
    selectMatchCompetitorsValid,
    selectRouteBySportEvent,
    (match, market, odds, tournament, competitors, route) => ({
        match,
        market,
        odds,
        tournament,
        competitors,
        route,
    })
);

const makePropsSelector = (): typeof selectProps => selectProps;

const useColorfulBanner = ({ matchId }: Input): Output => {
    const {
        competitors,
        market,
        route,
        tournament,
        match,
        odds,
    } = useMemoSelector(makePropsSelector(), { matchId });
    const isLive = !!match && LIVE_SPORTEVENT_STATUSES.includes(match.status);

    return {
        isLive,
        competitors,
        match,
        market,
        route,
        odds,
        tournament,
    };
};

export default useColorfulBanner;
